'use server';

import { headers } from 'next/headers';
import { redirect } from 'next/navigation';
import { prisma } from '@/lib/db/client';
import { verifyPassword } from './password';
import { createSession, destroyCurrentSession, setSessionCookie, loadSession } from './session';
import { writeAudit } from '@/lib/db/audit';
import { enforceRateLimit, RATE_LIMITS } from '@/lib/rate-limit';
import { actionError, toActionResult, type ActionResult } from '@/lib/errors';
import { loginSchema } from '@/lib/validation/auth';
import { formDataToObject } from '@/lib/validation/parse';

export async function loginAction(
  _prev: unknown,
  formData: FormData,
): Promise<ActionResult> {
  try {
    const parsed = loginSchema.safeParse(formDataToObject(formData));
    if (!parsed.success) {
      return actionError('E-Mail oder Passwort fehlt.', 'VALIDATION');
    }
    const { email, password } = parsed.data;

    // Throttle by email to slow credential stuffing.
    enforceRateLimit(`login:${email.toLowerCase()}`, RATE_LIMITS.login);

    const user = await prisma.user.findUnique({ where: { email: email.toLowerCase() } });
    const valid = user ? await verifyPassword(password, user.passwordHash) : false;

    // Generic message — never reveal whether the email exists.
    if (!user || !valid || !user.active) {
      return actionError('E-Mail oder Passwort ist falsch.', 'UNAUTHENTICATED');
    }

    const h = await headers();
    const token = await createSession(user.id, {
      userAgent: h.get('user-agent'),
      ipAddress: h.get('x-forwarded-for')?.split(',')[0]?.trim() ?? null,
    });
    await setSessionCookie(token);
    await writeAudit({
      organizationId: user.organizationId,
      userId: user.id,
      action: 'LOGIN',
      entityType: 'User',
      entityId: user.id,
    });
  } catch (err) {
    return toActionResult(err);
  }
  redirect('/dashboard');
}

export async function logoutAction(): Promise<void> {
  const session = await loadSession();
  if (session) {
    await writeAudit({
      organizationId: session.user.organizationId,
      userId: session.user.id,
      action: 'LOGOUT',
      entityType: 'User',
      entityId: session.user.id,
    }).catch(() => {});
  }
  await destroyCurrentSession();
  redirect('/login');
}
